<template>
  <div class="flex items-center">
    <NBox
      class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer"
      @click="openOfflineOrderDescriptionDialog"
    >
      <div
        class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
      >
        <img src="@/assets/images/moon.png" alt="offline" class="w-3 h-3" />
      </div>

      <NText
        variant="caption1"
        weight="bold"
        class="mr-2 text-neutral-secondary"
      >
        {{ t('orders.whatIsOfflineTrading') }}
      </NText>
    </NBox>

    <NBox
      class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mr-2 flex-1"
      @click="openAlgorithmOrderRiskDescriptionDialog('offline')"
    >
      <div
        class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
      >
        <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
      </div>

      <NText
        variant="caption1"
        weight="bold"
        class="mr-2 text-neutral-secondary"
      >
        {{ t('orders.riskAnnouncement.title') }}
      </NText>
    </NBox>
  </div>

  <VeeForm v-slot="{ errors, handleSubmit }" as="">
    <form
      autocomplete="off"
      class="p-4 rounded-md mt-2 w-full overflow-auto"
      :class="
        isOrderBuy
          ? 'bg-brand-primary bg-opacity-10 border border-brand-primary border-opacity-10'
          : 'bg-critical-secondary bg-opacity-90 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 darkTheme:bg-opacity-50'
      "
      @submit="handleSubmit($event, onSubmit)"
    >
      <!-- Bonus Share -->
      <template v-if="!isOrderBuy && Boolean(bonusShare)">
        <div
          class="flex justify-start items-center gap-1 mb-2 flex-1 cursor-pointer"
          @click="
            openBonusShareDialog(getSymbolName(symbol), bonusShare?.quantity)
          "
        >
          <NegarErrorBoldIcon size="16" class="text-warning-secondary" />

          <NText
            variant="overline"
            class="text-neutral-secondary whitespace-nowrap text-ellipsis overflow-hidden"
          >
            {{ t('سهام جایزه شما تا زمان تبدیل شدن') }}
            <NText variant="overline" weight="bold">
              {{ t('غیر قابل معامله') }}
            </NText>
            {{ t('است.') }}
          </NText>

          <NegarArrowLeftIcon size="16" />
        </div>
      </template>

      <div class="flex flex-col mb-2">
        <div class="flex gap-1 mb-1">
          <NPopper
            v-model="isOpenPriceTypeDropDown"
            placement="bottom-end"
            offset-y="2"
          >
            <div
              class="w-[40%] flex items-center cursor-pointer border transition rounded-md p-3 h-10 shadow-medium bg-neutral-primary text-neutral-primary border-neutral-secondary hover:border-brand-primary active:border-brand-primary"
            >
              <NText
                variant="caption1"
                weight="medium"
                class="pl-1 whitespace-nowrap text-neutral-secondary"
              >
                {{
                  isMarketPrice
                    ? OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.label
                    : OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.label
                }}
              </NText>

              <NegarArrowUpIcon
                class="inline transition duration-100"
                size="16"
                :class="{ 'rotate-180': !isOpenPriceTypeDropDown }"
              />
            </div>

            <template #content>
              <NTransitionFade>
                <NBox
                  class="overflow-auto w-[286px] cursor-pointer relative z-40"
                  rounded
                  border
                >
                  <NBox
                    v-gid="{
                      module: 'order',
                      screen: 'home',
                      section: 'offlineOrderDialog',
                      component: 'desiredPriceOfflineOrderButton',
                    }"
                    class="px-3 py-2 border-b border-neutral-secondary"
                    @click="
                      changePriceType(
                        OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.value
                      )
                    "
                  >
                    <div class="flex">
                      <NegarTikBoldIcon
                        v-if="!isMarketPrice"
                        size="18"
                        class="mb-1 text-neutral-tertiary ml-1"
                      />
                      <NText weight="bold" variant="caption1">
                        {{
                          OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.label
                        }}
                      </NText>
                    </div>

                    <i18n-t
                      scope="global"
                      keypath="modules.order.priceType.desiredPrice.description"
                      tag="span"
                      class="text-c2 font-normal"
                    >
                      <template #priceYouSetYourself>
                        <NText weight="bold" variant="caption2">
                          {{
                            t(
                              'modules.order.priceType.desiredPrice.priceYouSetYourself'
                            )
                          }}
                        </NText>
                      </template>
                    </i18n-t>
                  </NBox>

                  <NBox
                    v-gid="{
                      module: 'order',
                      screen: 'home',
                      section: 'offlineOrderDialog',
                      component: 'marketPriceOfflineOrderButton',
                    }"
                    class="px-3 py-2 border-b border-neutral-secondary"
                    @click="
                      changePriceType(
                        OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.value
                      )
                    "
                  >
                    <div class="flex">
                      <NegarTikBoldIcon
                        v-if="isMarketPrice"
                        size="18"
                        class="mb-1 text-neutral-tertiary ml-1"
                      />
                      <NText weight="bold" variant="caption1">
                        {{ OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.label }}
                      </NText>
                    </div>

                    <i18n-t
                      scope="global"
                      keypath="modules.order.priceType.marketPrice.description"
                      tag="span"
                      class="text-c2 font-normal"
                    >
                      <template #bestPriceAvailableOnOrders>
                        <NText weight="bold" variant="caption2">
                          {{
                            t(
                              'modules.order.priceType.marketPrice.bestPriceAvailableOnOrders'
                            )
                          }}
                        </NText>
                      </template>
                    </i18n-t>
                  </NBox>
                </NBox>
              </NTransitionFade>
            </template>
          </NPopper>

          <div
            v-if="isMarketPrice"
            class="flex items-center h-10 w-full rounded-md p-3 bg-neutral-primary-disable border border-neutral-primary-disable"
          >
            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-primary-disable"
            >
              {{ t('modules.order.bestMarketPrice') }}
            </NText>
          </div>

          <VeeField
            v-else
            v-slot="{ field }"
            v-model="formModel.price"
            name="price"
            :rules="{
              required: [t('common.price')],
            }"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              v-focus
              :model-value="formModel.price"
              input-class="text-center font-bold"
              v-bind="field"
              :error="errors.price"
              :required-sign="false"
              type="tel"
              dir="ltr"
              required
              class="!w-[24rem] !mb-0"
              size="small"
              :min="0"
              :step="symbol.fixedPriceTick"
              @focusin="$event.target.select()"
              @update:model-value="changePrice($event)"
            >
              <template #postfix>
                <NText variant="caption1" class="text-neutral-secondary">
                  {{ t('misc.rial') }}
                </NText>
              </template>
            </NInput>
          </VeeField>
        </div>
        <ErrorMessage v-slot="{ message }" name="price">
          <NText
            weight="regular"
            variant="caption2"
            class="text-critical-primary"
          >
            {{ message }}
          </NText>
        </ErrorMessage>
      </div>

      <template v-if="!isMarketPrice">
        <VeeField
          v-slot="{ field }"
          v-model="formModel.quantity"
          name="quantity"
          :rules="quantityValidationRules"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            :model-value="formModel.quantity"
            input-class="text-center font-bold"
            v-bind="field"
            :error="errors.quantity"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            :step="symbol.lotSize"
            :min="0"
            size="small"
            required
            @focusin="$event.target.select()"
            @update:model-value="changeQuantity($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.quantity') }}
              </NText>
            </template>
          </NInput>
        </VeeField>

        <div v-if="!isOrderBuy">
          <div class="flex justify-between items-center px-3">
            <NText variant="caption1">
              {{ `${t('modules.order.portfolio_quantity')}:` }}
            </NText>

            <NumberFormatter #="{ number }" :value="portfolioQuantity">
              <NText
                variant="caption1"
                class="cursor-pointer"
                @click="changeQuantity(portfolioQuantity)"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <Slider
          v-if="!isOrderBuy && portfolioQuantity"
          :model-value="formModel.quantity ?? 0"
          :max-value="portfolioQuantity"
          color="red"
          @change="changeQuantity"
        />

        <VeeField
          v-slot="{ field }"
          v-model="formModel.totalValue"
          name="totalValue"
          :rules="{ required: [t('misc.value')] }"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            :model-value="formModel.totalValue"
            v-bind="field"
            input-class="text-center font-bold"
            :error="errors.totalValue"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            required
            :min="0"
            size="small"
            @focusin="$event.target.select()"
            @update:model-value="changeTotalValue($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.value') }}
              </NText>
            </template>

            <template #postfix>
              <NText variant="caption1" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </template>
          </NInput>
        </VeeField>

        <template v-if="isOrderBuy">
          <div
            class="flex justify-between items-center px-3"
            :class="{
              'text-neutral-tertiary-disable': cash.buyingPower <= 0,
            }"
          >
            <NText variant="caption1">
              {{ t('common.buy_power') }}
            </NText>

            <NLoading v-if="isLoadingCash" class="text-brand-primary" />

            <div v-else>
              <NumberFormatter #="{ number }" :value="cash.buyingPower">
                <NText
                  dir="ltr"
                  variant="caption1"
                  class="cursor-pointer"
                  :class="{ 'cursor-default': cash.buyingPower <= 0 }"
                  @click="changeTotalValue(cash.buyingPower)"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>

              <NText
                variant="caption2"
                :class="{
                  'text-neutral-tertiary-disable': cash.buyingPower <= 0,
                }"
                class="text-neutral-secondary ms-0.5 align-middle"
              >
                {{ t('misc.rial') }}
              </NText>
            </div>
          </div>

          <Slider
            v-if="cash.buyingPower && cash.buyingPower > 0"
            :model-value="formModel.totalValue ?? 0"
            :max-value="cash.buyingPower"
            @change="changeTotalValue"
          />
        </template>
      </template>

      <template v-else>
        <NRadioGroup
          v-model="orderCondition"
          :required-sign="false"
          required
          class="!mb-0"
        >
          <div class="flex flex-col mb-3">
            <NRadio
              class="relative w-full"
              :value="ALGORITHM_ORDER_CONDITIONS.baseOnQuantity"
              @click="formModel.totalValue = null"
            >
              <VeeField
                v-slot="{ field }"
                v-model="formModel.quantity"
                name="quantity"
                :rules="quantityValidationRules"
              >
                <NInput
                  v-mask="{
                    alias: 'positiveNumber',
                  }"
                  v-focus
                  :model-value="formModel.quantity"
                  input-class="text-center font-bold"
                  v-bind="field"
                  :error="errors.quantity"
                  :required-sign="false"
                  type="tel"
                  dir="ltr"
                  :step="symbol.lotSize"
                  :min="0"
                  size="small"
                  required
                  :disabled="!isSelectedQuantity"
                  @focusin="$event.target.select()"
                  @update:model-value="changeQuantity($event)"
                >
                  <template #prefix>
                    <NText
                      variant="caption1"
                      weight="medium"
                      class="text-neutral-primary"
                    >
                      {{ t('misc.quantity') }}
                    </NText>
                  </template>
                </NInput>
              </VeeField>
            </NRadio>
            <ErrorMessage v-slot="{ message }" name="quantity">
              <NText
                weight="regular"
                variant="caption2"
                class="text-critical-primary pr-8"
              >
                {{ message }}
              </NText>
            </ErrorMessage>
          </div>

          <div v-if="!isOrderBuy">
            <div class="flex justify-between items-center px-3">
              <NText
                variant="caption1"
                :class="
                  isSelectedQuantity
                    ? 'text-neutral-primary'
                    : 'text-neutral-primary-disable'
                "
              >
                {{ `${t('modules.order.portfolio_quantity')}:` }}
              </NText>

              <NumberFormatter #="{ number }" :value="portfolioQuantity">
                <NText
                  variant="caption1"
                  :class="
                    isSelectedQuantity
                      ? 'text-neutral-primary cursor-pointer'
                      : 'text-neutral-primary-disable cursor-default'
                  "
                  @click="changeQuantity(portfolioQuantity)"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </div>
          </div>

          <Slider
            v-if="!isOrderBuy && portfolioQuantity"
            :model-value="formModel.quantity ?? 0"
            :max-value="portfolioQuantity"
            color="red"
            :disabled="!isSelectedQuantity"
            @change="changeQuantity"
          />

          <div class="flex flex-col mb-3">
            <NRadio
              class="relative w-full mt-1 flex items-center"
              :value="ALGORITHM_ORDER_CONDITIONS.baseOnTotalValue"
              @click="formModel.quantity = null"
            >
              <VeeField
                v-slot="{ field }"
                v-model="formModel.totalValue"
                name="totalValue"
                :rules="totalValueValidationRules"
              >
                <NInput
                  v-mask="{
                    alias: 'positiveNumber',
                  }"
                  :model-value="formModel.totalValue"
                  v-bind="field"
                  input-class="text-center font-bold"
                  :error="errors.totalValue"
                  :required-sign="false"
                  :show-error="false"
                  type="tel"
                  dir="ltr"
                  required
                  :min="0"
                  size="small"
                  :disabled="isSelectedQuantity"
                  @focusin="$event.target.select()"
                  @update:model-value="changeTotalValue($event)"
                >
                  <template #prefix>
                    <NText
                      variant="caption1"
                      weight="medium"
                      class="text-neutral-primary"
                    >
                      {{ t('misc.value') }}
                    </NText>
                  </template>

                  <template #postfix>
                    <NText variant="caption1" class="text-neutral-secondary">
                      {{ t('misc.rial') }}
                    </NText>
                  </template>
                </NInput>
              </VeeField>
            </NRadio>
            <ErrorMessage v-slot="{ message }" name="totalValue">
              <NText
                weight="regular"
                variant="caption2"
                class="text-critical-primary pr-8"
              >
                {{ message }}
              </NText>
            </ErrorMessage>
          </div>

          <template v-if="isOrderBuy">
            <div class="flex justify-between items-center px-3">
              <NText
                variant="caption1"
                :class="
                  isSelectedQuantity || cash.buyingPower <= 0
                    ? 'text-neutral-primary-disable'
                    : 'text-neutral-primary'
                "
              >
                {{ t('common.buy_power') }}
              </NText>

              <NLoading v-if="isLoadingCash" class="text-brand-primary" />

              <div v-else>
                <NumberFormatter #="{ number }" :value="cash.buyingPower">
                  <NText
                    dir="ltr"
                    variant="caption1"
                    weight="bold"
                    :class="
                      isSelectedQuantity || cash.buyingPower <= 0
                        ? 'text-neutral-primary-disable cursor-default'
                        : 'text-neutral-primary cursor-pointer'
                    "
                    @click="changeTotalValue(cash.buyingPower)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>

                <NText
                  variant="caption2"
                  class="ms-0.5 align-middle"
                  :class="
                    isSelectedQuantity || cash.buyingPower <= 0
                      ? 'text-neutral-primary-disable'
                      : 'text-neutral-secondary'
                  "
                >
                  {{ t('misc.rial') }}
                </NText>
              </div>
            </div>

            <Slider
              v-if="cash.buyingPower && cash.buyingPower > 0"
              :model-value="formModel.totalValue ?? 0"
              :max-value="cash.buyingPower"
              :disabled="isSelectedQuantity"
              @change="changeTotalValue"
            />
          </template>
        </NRadioGroup>
      </template>

      <div class="flex items-center justify-center p-2 rounded-md mb-32">
        <NText>
          {{ validityDateLabel }}
        </NText>
      </div>

      <div
        class="bg-neutral-primary border-t border-t-neutral-primary w-full p-3 rounded-b-md absolute bottom-0 right-0 z-20"
        :class="
          isOrderBuy
            ? 'border border-brand-primary border-opacity-10'
            : 'border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10'
        "
      >
        <NBox
          v-if="PETROCHEMICALS_ISINS.includes(symbol.isin!)"
          class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mb-6"
          @click="openPetrochemicalsOrderRiskDescriptionDialog()"
        >
          <div
            class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
          >
            <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
          </div>

          <NText
            variant="caption1"
            weight="bold"
            class="mr-2 text-neutral-secondary"
          >
            {{ t('orders.symbolUnderCautionTitle') }}
          </NText>
        </NBox>

        <div class="flex gap-2 items-center justify-center">
          <NButton
            v-if="isOrderBuy"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'buyOfflineOrderDialog',
              component: 'submitOfflineOrderButton',
              disabled: isLoadingCreateOfflineOrder || isLoadingAgreements,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            size="small"
            :loading="isLoadingCreateOfflineOrder"
            :disabled="isLoadingCreateOfflineOrder || isLoadingAgreements"
          >
            {{ t('modules.order.sendBuyOrder') }}
          </NButton>

          <NButton
            v-else
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'sellOfflineOrderDialog',
              component: 'submitOfflineOrderButton',
              disabled: isLoadingCreateOfflineOrder || isLoadingAgreements,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            color="red"
            size="small"
            :loading="isLoadingCreateOfflineOrder"
            :disabled="isLoadingCreateOfflineOrder || isLoadingAgreements"
          >
            {{ t('modules.order.sendSellOrder') }}
          </NButton>

          <NButton
            v-gid="{
              module: 'order',
              screen: 'home',
              section: `${type}OfflineOrderDialog`,
              component: 'sendAndCloseButton',
              disabled: isLoadingCreateOfflineOrder || isLoadingAgreements,
            }"
            variant="outline"
            class="font-bold text-c1"
            :color="isOrderBuy ? 'green' : 'red'"
            size="small"
            :loading="isLoadingCreateOfflineOrder"
            :disabled="isLoadingCreateOfflineOrder || isLoadingAgreements"
            @click="
              () => {
                shouldCloseDialogAfterOrderExecution = true;
                handleSubmit();
              }
            "
          >
            {{ t('modules.order.sendAndClose') }}
          </NButton>
        </div>
      </div>
    </form>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NegarErrorBoldIcon,
  NegarArrowUpIcon,
  NegarTikBoldIcon,
  NegarArrowLeftIcon,
} from '@negar/icons';
import { useLiveCashQuery } from '@core/cash';
import {
  useSymbolDeductions,
  calcSymbolBuyValue,
  calcSymbolQuantityBuy,
  calcSymbolQuantitySell,
  calcSymbolSellValue,
  PETROCHEMICALS_ISINS,
  getSymbolName,
} from '@core/symbol';
import {
  ALGORITHM_ORDER_CONDITIONS,
  OFFLINE_ORDER_EXECUTION_TYPE,
} from '@core/order';
import { omitBy } from '@libs/utils';
import { Instrument, PortfolioItem } from '@libs/api-reference';
import { useDialog } from '@negar/core';

import {
  useCreateOrder,
  useOrderDialogs,
  OFFLINE_ORDER_PRICE_TYPE_OPTIONS,
  TCreateMarketOrderArguments,
  TCreateOfflineOrderArguments,
} from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';
import Slider from '@/components/Slider.vue';

import vMask from '@/directives/mask';
import vFocus from '@/directives/focus';

import eventBus from '@/composables/event/eventBus';
import { usePortfolioDialogs } from '@/modules/portfolio';
import { ErrorMessage } from 'vee-validate';

type TFormModel = {
  quantity?: number | null;
  price?: number | null;
  totalValue?: number | null;
  executedQuantity: number | null;
  validityDate: Date;
  orderExecutionType?: string;
};

type TOrderFormProps = {
  type: 'sell' | 'buy';
  symbol: Instrument;
  portfolioQuantity: number;
  model: {
    quantity?: null | number;
    price?: null | number;
  };
  dialogId?: string;
  bonusShare?: PortfolioItem['bonusShare'];
};

const props = withDefaults(defineProps<TOrderFormProps>(), {
  dialogId: undefined,
  bonusShare: undefined,
});

const { t, d } = useI18n();

const { selectedDialogId } = useDialog();
const { openBonusShareDialog } = usePortfolioDialogs();

const { cashData: cash, isLoadingCash } = useLiveCashQuery();

const { findWageConfigBySymbol } = useSymbolDeductions();

const wage = findWageConfigBySymbol(props.symbol);

const {
  openOfflineOrderDescriptionDialog,
  openAlgorithmOrderRiskDescriptionDialog,
  openOfflineConfirmDialog,
  openPetrochemicalsOrderRiskDescriptionDialog,
} = useOrderDialogs();

const today = new Date();

const shouldCloseDialogAfterOrderExecution = ref(false);

const priceType = ref<'desiredPrice' | 'marketPrice'>(
  OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.value
);
const orderCondition = ref<string>(ALGORITHM_ORDER_CONDITIONS.baseOnQuantity);

const isOpenPriceTypeDropDown = ref(false);

const isMarketPrice = computed(() => {
  return priceType.value === OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.value;
});

const BASE_ORDER_FORM: TFormModel = {
  quantity: null,
  price: null,
  totalValue: null,
  executedQuantity: props.symbol.maximumOrderQuantity,
  validityDate: new Date(today.setDate(today.getDate() + 7)),
};

const formModel = reactive<TFormModel>({
  ...BASE_ORDER_FORM,
  orderExecutionType: OFFLINE_ORDER_EXECUTION_TYPE.desired.helium,
});

const isOrderBuy = computed(() => {
  return props.type === 'buy';
});

const isSelectedQuantity = computed(() => {
  return orderCondition.value === ALGORITHM_ORDER_CONDITIONS.baseOnQuantity;
});

const {
  createOfflineOrder,
  createMarketOrder,
  isLoadingCreateOfflineOrder,
  isLoadingAgreements,
} = useCreateOrder({
  isin: props.symbol.isin as string,
  orderType: props.type,
});

const minOrderValue = computed(() => {
  if (isOrderBuy.value) {
    return props.symbol.minBuyOrderValue;
  } else {
    return props.symbol.minSellOrderValue;
  }
});

const realValue = computed(() => {
  if (isOrderBuy.value) {
    return calcSymbolBuyValue(formModel, wage);
  } else {
    return calcSymbolSellValue(formModel, wage);
  }
});

const quantity = computed(() => {
  let roundUp;

  if (formModel.totalValue) {
    roundUp = formModel.totalValue >= cash.value.buyingPower;
  }

  if (isOrderBuy.value) {
    return calcSymbolQuantityBuy(formModel, wage, roundUp);
  } else {
    return calcSymbolQuantitySell(formModel, wage, roundUp);
  }
});

const changePriceType = (newValue: 'desiredPrice' | 'marketPrice') => {
  priceType.value = newValue;

  Object.assign(formModel, BASE_ORDER_FORM);

  orderCondition.value = ALGORITHM_ORDER_CONDITIONS.baseOnQuantity;
  formModel.orderExecutionType =
    OFFLINE_ORDER_PRICE_TYPE_OPTIONS[newValue].executionType;

  isOpenPriceTypeDropDown.value = false;
};

const changeTotalValue = (newValue: number) => {
  if ((isMarketPrice.value && isSelectedQuantity.value) || newValue < 0) return;

  formModel.totalValue = newValue;

  if (formModel.price) {
    formModel.quantity = quantity.value;
  }
};

const changePrice = (newValue: number) => {
  formModel.price = newValue;

  if (formModel.quantity) {
    formModel.totalValue = realValue.value;
  } else if (formModel.totalValue) {
    formModel.quantity = quantity.value;
  }
};

const changeQuantity = (newValue: number) => {
  if (isMarketPrice.value && !isSelectedQuantity.value) return;

  formModel.quantity = newValue;

  if (formModel.price) {
    formModel.totalValue = realValue.value;
  }
};

const validityDateLabel = computed(
  () => `${t('orders.valid_until')} ${d(formModel.validityDate, 'shortDate')}`
);

const handleCreateOfflineOrder = (model, resetForm) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_ORDER_FORM);
    resetForm({
      values: { ...BASE_ORDER_FORM },
    });
  };

  if (isMarketPrice.value) {
    createMarketOrder({
      formModel: formModel as TCreateMarketOrderArguments['formModel'],
      resetFormModel,
      shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
      isMarketOrderBasedOnQuantity: isSelectedQuantity.value,
    });

    return;
  }

  createOfflineOrder({
    formModel: formModel as TCreateOfflineOrderArguments['formModel'],
    resetFormModel,
    shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
  });
};

const onSubmit = (model, { resetForm }) => {
  if (isMarketPrice.value) {
    return openOfflineConfirmDialog({
      instrumentName: props.symbol.instrumentName,
      type: props.type,
      createOfflineOrder: () => handleCreateOfflineOrder(model, resetForm),
    });
  }

  handleCreateOfflineOrder(model, resetForm);
};

eventBus.on('executeOrder', (shouldClose) => {
  if (
    selectedDialogId.value === props.dialogId &&
    !isLoadingCreateOfflineOrder.value
  ) {
    shouldCloseDialogAfterOrderExecution.value = shouldClose;

    onSubmit(formModel, {
      resetForm: () => {
        Object.assign(formModel, BASE_ORDER_FORM);
      },
    });
  }
});

watch(
  () => props.model,
  (newModel) => {
    if (isMarketPrice.value) return;

    const sanitizedModel = omitBy(
      newModel,
      (v) => v === null || v === undefined
    );

    if (Object.keys(sanitizedModel).length) {
      Object.assign(formModel, sanitizedModel);
    }

    if (formModel.price) {
      changePrice(formModel.price);
    }

    if (formModel.quantity) {
      changeQuantity(formModel.quantity);
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

type TValidationRules = {
  required?: boolean | string[];
  min?: [number, string, string];
  upperBound?: [number, string, string];
};

const totalValueValidationRules = computed<TValidationRules>(() => {
  if (!isSelectedQuantity.value) {
    const rules: TValidationRules = {
      required: [t('misc.value')],
    };

    if (isOrderBuy.value) {
      rules.upperBound = [
        cash.value.buyingPower,
        t('misc.value'),
        t('common.buy_power'),
      ];
    }

    if (isOrderBuy.value || props.portfolioQuantity !== formModel.quantity) {
      rules.min = [
        minOrderValue.value,
        t('modules.order.order_value'),
        t('misc.rial'),
      ];
    }

    return rules;
  }

  return {};
});

const quantityValidationRules = computed<TValidationRules>(() => {
  if (isSelectedQuantity.value) {
    const rules: TValidationRules = {
      required: [t('misc.quantity')],
    };

    if (!isOrderBuy.value) {
      rules.upperBound = [
        props.portfolioQuantity,
        t('misc.quantity'),
        t('modules.order.portfolio_quantity'),
      ];
    }

    return rules;
  }

  return {};
});
</script>
